import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { wait } from 'utils/wait';
import { hasGrantedConsentDefaultInDataLayer } from './has-granted-consent-default-in-data-layer';

export interface CookiebotConsent {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

export type CookiebotCategory = keyof CookiebotConsent;

const CONSENT_POPUP_ID = 'CybotCookiebotDialog';
const CONSENT_POPUP_BLOCKED_TIMEOUT_MS = 8000;
const CONSENT_UPDATE_POLL_INTERVAL_MS = 100;
const CONSENT_UPDATE_POLL_TIMEOUT_MS = 1000;

const COOKIEBOT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
  'CookiebotOnRenew',
];

const hasConsentUpdateInDataLayer = (): boolean => {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return false;
  }

  return window.dataLayer.some((entry) => {
    const consentEntry = entry as unknown as ArrayLike<unknown> | undefined;
    return consentEntry?.[0] === 'consent' && consentEntry?.[1] === 'update';
  });
};

const waitForConsentUpdateInDataLayer = (): Promise<void> =>
  new Promise((resolve) => {
    if (hasConsentUpdateInDataLayer()) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (hasConsentUpdateInDataLayer()) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= CONSENT_UPDATE_POLL_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        resolve();
      }
    }, CONSENT_UPDATE_POLL_INTERVAL_MS);
  });

const getCookiebotConsent = (): CookiebotConsent => {
  const consent = typeof window !== 'undefined' ? window['Cookiebot']?.consent : undefined;

  return {
    necessary: Boolean(consent?.necessary),
    preferences: Boolean(consent?.preferences),
    statistics: Boolean(consent?.statistics),
    marketing: Boolean(consent?.marketing),
  };
};

export const useCookiebotCategoryConsent = (category: CookiebotCategory): boolean =>
  useCookiebotConsent()[category];

export const useCookiebotConsent = (): CookiebotConsent => {
  const [consent, setConsent] = useState<CookiebotConsent>(getCookiebotConsent);

  useEffect(() => {
    const checkConsent = () => {
      setConsent(getCookiebotConsent());
    };

    checkConsent();

    COOKIEBOT_EVENTS.forEach((callback) => window.addEventListener(callback, checkConsent));

    return () => {
      COOKIEBOT_EVENTS.forEach((callback) => window.removeEventListener(callback, checkConsent));
    };
  }, []);

  return consent;
};

const RETRIES = 5;
const INTERVAL = 1000;

/**
 * Hook to add overflow-hidden class to the html element when Cookiebot is active.
 * This prevents scrolling when the cookie dialog is active.
 * The scroll lock is removed when consent is given or when the component unmounts.
 */
export const useLockScrollWhenCookieBotActive = (): void => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window['Cookiebot']) {
      const { hasResponse } = window['Cookiebot'];

      // If user has already responded, no need to proceed
      if (hasResponse === true) return;

      // Function to apply the scroll lock
      const applyScrollLock = (): (() => void) => {
        // Add overflow hidden to prevent scrolling
        document.documentElement.classList.add('overflow-hidden');

        // Cleanup function for scroll lock
        const cleanup = () => {
          document.documentElement.classList.remove('overflow-hidden');
        };

        // Remove scroll lock when user responds (either accept or decline)
        window['CookiebotCallback_OnAccept'] = cleanup;
        window['CookiebotCallback_OnDecline'] = cleanup;

        return cleanup;
      };

      // Function to try finding the banner with retries
      const tryFindBanner = (
        retries = RETRIES,
        interval = INTERVAL,
        timeoutIds: Set<NodeJS.Timeout> = new Set()
      ): Promise<HTMLElement | null> => {
        return new Promise((resolve) => {
          const cookiebotBanner = document.getElementById('cookiebanner');

          if (cookiebotBanner) {
            resolve(cookiebotBanner);
            return;
          }

          if (retries <= 0) {
            resolve(null);
            return;
          }

          const timeoutId = setTimeout(() => {
            timeoutIds.delete(timeoutId);
            tryFindBanner(retries - 1, interval, timeoutIds).then(resolve);
          }, interval);
          timeoutIds.add(timeoutId);
        });
      };

      // Start the retry mechanism if user hasn't responded
      if (hasResponse !== undefined) {
        let cleanup: (() => void) | undefined;
        const timeoutIds = new Set<NodeJS.Timeout>();

        tryFindBanner(RETRIES, INTERVAL, timeoutIds).then((cookiebotBanner) => {
          if (cookiebotBanner) {
            cleanup = applyScrollLock();
          }
        });

        // Return cleanup function for component unmount
        return () => {
          // Cancel any pending timeouts
          timeoutIds.forEach(clearTimeout);
          timeoutIds.clear();

          if (cleanup) {
            cleanup();
          } else {
            // Fallback cleanup in case the banner was never found
            document.documentElement.classList.remove('overflow-hidden');
          }
        };
      }
    }

    return () => {
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, []);
};

export const getConsentPopupBlockedPromise = () =>
  new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined' || window['Cookiebot']?.hasResponse === true) {
      reject();
      return;
    }

    wait(CONSENT_POPUP_BLOCKED_TIMEOUT_MS).then(() => {
      const hasConsentResponse = window['Cookiebot']?.hasResponse === true;

      if (!document.getElementById(CONSENT_POPUP_ID) && !hasConsentResponse) {
        resolve();
      } else {
        reject();
      }
    });
  });

if (typeof window !== 'undefined') {
  getConsentPopupBlockedPromise()
    .then(function grantConsentWhenPopupBlocked() {
      const gtag = (window as unknown as Window & { gtag: (...args: unknown[]) => void }).gtag;
      gtag('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    })
    .catch(noop);
}

export const getConsentSettledPromise = (): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject();
      return;
    }

    if (hasConsentUpdateInDataLayer() || hasGrantedConsentDefaultInDataLayer()) {
      resolve();
      return;
    }

    const resolveAfterConsentUpdate = (): void => {
      waitForConsentUpdateInDataLayer().then(() => resolve());
    };

    if (window['Cookiebot']?.hasResponse === true) {
      resolveAfterConsentUpdate();
      return;
    }

    const handler = (): void => {
      COOKIEBOT_EVENTS.forEach((callback) => window.removeEventListener(callback, handler));
      resolveAfterConsentUpdate();
    };

    COOKIEBOT_EVENTS.forEach((callback) => window.addEventListener(callback, handler));
  });
